import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchSayingById, fetchTranslations, fetchLanguages } from '../api';
import { Button } from '../components/ui/button';

function SayingDetailPage() {
  const { sayingId } = useParams();
  const navigate = useNavigate();
  const [saying, setSaying] = useState(null);
  const [translations, setTranslations] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [sayingData, languagesData] = await Promise.all([
          fetchSayingById(sayingId),
          fetchLanguages()
        ]);
        setSaying(sayingData);
        setLanguages(languagesData);

        // Fetch all translations for this saying
        const translationsData = await fetchTranslations({ original_saying_id: sayingId });
        setTranslations(translationsData);
        setError(null);
      } catch (err) {
        console.error('Error loading saying details:', err);
        setError('Failed to load saying details.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [sayingId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center text-destructive">{error}</div>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  if (!saying) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center">Saying not found</div>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      <Button onClick={() => navigate(-1)} variant="outline" className="mb-4">
        ← Back to Sayings
      </Button>

      {/* Original Saying */}
      <div className="bg-card p-6 rounded-lg shadow-sm max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold mb-2">Original Saying</h2>
        <p className="text-lg mb-4">{saying.saying}</p>
        <div className="text-sm text-muted-foreground">
          <p>Category: {saying.category_name}</p>
          <p>Language: {languages.find(l => l.language_id === saying.language_id)?.name}</p>
        </div>
      </div>

      {/* Translations Section */}
      <div className="max-w-2xl mx-auto">
        <h3 className="text-xl font-semibold mb-4">Translations</h3>
        {translations.length === 0 ? (
          <p className="text-center text-muted-foreground">No translations available yet.</p>
        ) : (
          <div className="grid gap-4">
            {translations.map((translation) => {
              const language = languages.find(l => l.language_id === translation.language_id);
              return (
                <div
                  key={translation.translation_id}
                  className="bg-card p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-lg mb-2">{translation.translated_text}</p>
                      {translation.explanation && (
                        <p className="text-sm text-muted-foreground italic mb-2">
                          <strong>Explanation:</strong> {translation.explanation}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Translated to: {language?.name}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default SayingDetailPage; 